import React, { useState, useEffect, useRef } from 'react';

const MapLocationPicker = ({ isOpen, onClose, onLocationSelected, initialLocation = null }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const searchBoxRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || {
    name: '',
    address: '',
    lat: 40.7128, // Default to NYC
    lng: -74.0060,
    place_id: ''
  });
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Load Google Maps API
  useEffect(() => {
    if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      // console.warn('Google Maps API key not configured. Map functionality will be limited.');
      return;
    }

    if (!window.google && isOpen) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsMapLoaded(true);
      };
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
      };
      document.head.appendChild(script);
    } else if (window.google && isOpen) {
      setIsMapLoaded(true);
    }
  }, [isOpen]);

  // Initialize map when ready
  useEffect(() => {
    if (isMapLoaded && isOpen && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
  }, [isMapLoaded, isOpen]);

  const initializeMap = () => {
    if (!window.google || !mapRef.current) return;

    // Create map
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: selectedLocation.lat, lng: selectedLocation.lng },
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    mapInstanceRef.current = map;

    // Create marker
    const marker = new window.google.maps.Marker({
      position: { lat: selectedLocation.lat, lng: selectedLocation.lng },
      map: map,
      draggable: true,
      title: 'Rehearsal Location'
    });

    markerRef.current = marker;

    // Handle marker drag
    marker.addListener('dragend', () => {
      const position = marker.getPosition();
      const lat = position.lat();
      const lng = position.lng();
      
      // Reverse geocode to get address
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setSelectedLocation(prev => ({
            ...prev,
            lat,
            lng,
            address: results[0].formatted_address,
            place_id: results[0].place_id
          }));
        }
      });
    });

    // Handle map click
    map.addListener('click', (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      
      marker.setPosition({ lat, lng });
      
      // Reverse geocode
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setSelectedLocation(prev => ({
            ...prev,
            lat,
            lng,
            address: results[0].formatted_address,
            place_id: results[0].place_id
          }));
        }
      });
    });

    // Create search box
    if (searchBoxRef.current) {
      const searchBox = new window.google.maps.places.SearchBox(searchBoxRef.current);
      
      // Bias search results to map viewport
      map.addListener('bounds_changed', () => {
        searchBox.setBounds(map.getBounds());
      });

      // Handle place selection
      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces();
        if (places.length === 0) return;

        const place = places[0];
        if (!place.geometry || !place.geometry.location) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Update marker and map
        marker.setPosition({ lat, lng });
        map.setCenter({ lat, lng });
        map.setZoom(17);

        // Update location
        setSelectedLocation(prev => ({
          ...prev,
          lat,
          lng,
          address: place.formatted_address || place.name,
          place_id: place.place_id || '',
          name: prev.name || place.name || ''
        }));
      });
    }
  };

  const handleSave = () => {
    if (selectedLocation.name && selectedLocation.address) {
      onLocationSelected({
        name: selectedLocation.name,
        address: selectedLocation.address,
        google_place_id: selectedLocation.place_id,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng,
        maps_url: `https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(4px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: '#1f2937',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '800px',
        height: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        border: '1px solid #374151'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
          color: '#ffffff',
          padding: '16px 24px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            📍 Select Rehearsal Location
          </h2>
          <button onClick={onClose} style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '6px',
            color: 'white',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '18px'
          }}>
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px' }}>
          {/* Location Details Form */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Location name *"
                value={selectedLocation.name}
                onChange={(e) => setSelectedLocation(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  color: '#f9fafb',
                  fontSize: '14px'
                }}
              />
              <input
                ref={searchBoxRef}
                type="text"
                placeholder="Search for a place..."
                style={{
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  color: '#f9fafb',
                  fontSize: '14px'
                }}
              />
            </div>
            <input
              type="text"
              placeholder="Address"
              value={selectedLocation.address}
              onChange={(e) => setSelectedLocation(prev => ({ ...prev, address: e.target.value }))}
              style={{
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                padding: '10px 12px',
                color: '#f9fafb',
                fontSize: '14px',
                width: '100%'
              }}
            />
          </div>

          {/* Map Container */}
          <div style={{
            flex: 1,
            backgroundColor: '#374151',
            borderRadius: '8px',
            border: '1px solid #4b5563',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
              <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af',
                fontSize: '14px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ marginBottom: '12px', fontSize: '24px' }}>🗺️</div>
                <div style={{ marginBottom: '8px', fontWeight: '500' }}>Google Maps API Key Required</div>
                <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                  To use interactive map location selection, please configure your Google Maps API key in the environment variables.
                  <br />
                  You can still manually enter location details above.
                </div>
              </div>
            ) : !isMapLoaded ? (
              <div style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af',
                fontSize: '14px'
              }}>
                Loading Google Maps...
              </div>
            ) : (
              <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
            )}
          </div>

          {/* Instructions */}
          <div style={{
            marginTop: '12px',
            color: '#9ca3af',
            fontSize: '12px',
            textAlign: 'center'
          }}>
            Click on the map or drag the marker to select a location. Use the search box to find specific places.
          </div>
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #374151',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button onClick={onClose} style={{
            padding: '10px 20px',
            backgroundColor: '#374151',
            color: '#d1d5db',
            border: '1px solid #4b5563',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            Cancel
          </button>
          <button 
            onClick={handleSave}
            disabled={!selectedLocation.name || !selectedLocation.address}
            style={{
              padding: '10px 20px',
              backgroundColor: selectedLocation.name && selectedLocation.address ? '#059669' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: selectedLocation.name && selectedLocation.address ? 'pointer' : 'not-allowed'
            }}
          >
            Save Location
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapLocationPicker;